<template>
  <div :class="$style.container">
    <Button
      :class="{ [$style.hidden]: firstStep }"
      :data-test-id="$testId('back')"
      hasOutline
      :href="prevNavPanelLink"
      :isDisabled="firstStep"
      target="_self"
      :text="$options.buttonLabels.back"
      @click.native="handleNextPage(false)"
    />
    <BreadcrumbLinks
      :class="$style.breadcrumbs"
      :currentStep="productTourStepIndex"
      :links="productTourLinks"
      @click="handleBreadcrumb"
    />
    <Button
      v-if="!lastStep"
      :data-test-id="$testId('next')"
      :href="nextNavPanelLink"
      target="_self"
      :text="$options.buttonLabels.next"
      @click.native="handleNextPage(true)"
    />
    <Button
      v-else
      :data-test-id="$testId('done')"
      :text="$options.buttonLabels.done"
      @click="handleComplete"
    />
  </div>
</template>

<script>
import { Button } from '@jumpcloud/ui-components';
import { mapActions, mapState } from 'vuex';
import { productTourLabels } from 'client/GrowthEngineering/utilities/GrowthConstants';
import BreadcrumbLinks from 'client/components/Breadcrumbs/BreadcrumbLinks.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import Util from 'client/util/Util';

import ProductTourMixin from './ProductTourMixin';
import ProductTourSteps, { stepIndexes } from './ProductTourSteps';

const onboardingModule = 'OnboardingModule';

const buttonLabels = {
  back: 'Back',
  done: productTourLabels.done,
  next: 'Next',
};

export default {
  name: 'ProductTourPagination',

  EventHelper,
  ProductTourSteps,
  Util,
  buttonLabels,

  components: {
    Button,
    BreadcrumbLinks,
  },

  mixins: [ProductTourMixin],

  computed: {
    ...mapState(onboardingModule, [
      'productTourStepIndex',
    ]),

    firstStep() {
      return this.productTourStepIndex === stepIndexes.modal;
    },

    lastStep() {
      return this.productTourStepIndex === this.$options.ProductTourSteps.length - 1;
    },

    nextNavPanelLink() {
      const nextProductTourStep = this.$options.ProductTourSteps[this.productTourStepIndex + 1];

      return nextProductTourStep?.navPanelLink?.href || '';
    },

    prevNavPanelLink() {
      if (this.productTourStepIndex <= 0) {
        return '';
      }

      return this.$options.ProductTourSteps[this.productTourStepIndex - 1].navPanelLink.href;
    },

    productTourLinks() {
      return this.$options.ProductTourSteps.map(step => step.navPanelLink);
    },
  },

  methods: {
    ...mapActions(onboardingModule, [
      'setProductTourStep',
      'setShowProductTour',
    ]),

    handleBreadcrumb(index) {
      // Relaunch modal
      if (index === 0) {
        this.handleInitialStep();
      } else if (this.productTourStepIndex === 0) {
        this.handleShowProductTour();
      }

      this.setProductTourStep(index);
    },

    handleComplete() {
      this.$options.EventHelper.triggerButtonClicked({
        location: productTourLabels.popoverLocation,
        text: productTourLabels.done,
      });

      this.setShowProductTour(false);
      this.setProductTourStep(stepIndexes.modal);
      // Route to Discover page when done
      this.$options.Util.redirect(
        this.$options.ProductTourSteps[stepIndexes.modal].navPanelLink.href,
      );
    },

    handleInitialStep() {
      this.setShowProductTour(false);
      this.openModal();
    },

    handleNextPage(shouldIncrementIndex) {
      // increment/decrement tour step
      const increment = shouldIncrementIndex ? 1 : -1;

      // Close modal and open popover when we click the next button from step 0
      if (this.productTourStepIndex === 0 && increment === 1) {
        this.handleShowProductTour();
      } else if (this.productTourStepIndex === 1 && increment === -1) {
        // Re-open modal if we click the previous button on step 1
        this.handleInitialStep();
      }

      this.setProductTourStep(this.productTourStepIndex + increment);
    },

    handleShowProductTour() {
      // Close modal/Open popover
      this.setShowProductTour(true);
      this.closeModal();
    },
  },
};
</script>

<style module scoped>
  .container {
    display: flex;
    justify-content: space-between;
  }

  .hidden {
    visibility: hidden;
  }

  .breadcrumbs {
    padding: 0 var(--jc-spacer-medium);
    width: 100%;
  }
</style>
